import React, { useEffect, useState } from 'react';

import LazyComponent from '@fd/ui/LazyComponent';

import { lazyWithRetry } from '../../helpers/utilities';
import ErrorBoundary from '../../layouts/Portal/ErrorBoundary';
import { waitForProviderReady } from '../../services/featureFlags/core/featureFlagClient';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagReady } from '../../services/featureFlags/useFeatureFlagReady';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { AppLoadingCircularProgress } from '../AppLoadingCircularProgress';

const DeliverySettings = lazyWithRetry(() => import('./DeliverySettings'));

type Page = 'micro-frontend' | 'legacy';

type Props = {
  backUrl: string; // The Portal page the MFE's back arrow goes to, as the legacy page's does.
  legacy: React.ReactNode; // The page the MFE replaces, shown while thirdPartyDeliverySettings is off.
};

/**
 * A property's or sales channel's delivery settings page: the delivery-settings MFE.
 *
 * Until thirdPartyDeliverySettings is removed, it shows the legacy page instead while the flag is
 * off. It waits for the flag so the legacy page doesn't flash first, and shows the legacy page if
 * the flag provider isn't ready in time, as it did before the flag. The first page shown stays for
 * the visit, so a flag change can't swap it while someone edits.
 */
const DeliverySettingsPage = ({ legacy, backUrl }: Props) => {
  const isThirdPartyDeliverySettingsOn = useFeatureFlagValue(
    PortalFeatureFlags.thirdPartyDeliverySettings,
    false
  );
  const isThirdPartyDeliverySettingsReady = useFeatureFlagReady(
    PortalFeatureFlags.thirdPartyDeliverySettings
  );
  const [shownPage, setShownPage] = useState<Page | null>(null);

  let flagPage: Page | null = null;
  if (isThirdPartyDeliverySettingsReady) {
    flagPage = isThirdPartyDeliverySettingsOn ? 'micro-frontend' : 'legacy';
  }
  const page = shownPage ?? flagPage;

  useEffect(() => {
    if (!shownPage && flagPage) {
      setShownPage(flagPage);
    }
  }, [shownPage, flagPage]);

  useEffect(() => {
    let isUnmounted = false;
    void waitForProviderReady({
      provider: PortalFeatureFlags.thirdPartyDeliverySettings.provider,
    }).then((isReady) => {
      if (!isReady && !isUnmounted) {
        setShownPage((current) => current ?? 'legacy');
      }
    });
    return () => {
      isUnmounted = true;
    };
  }, []);

  if (!page) {
    return <AppLoadingCircularProgress />;
  }

  if (page === 'legacy') {
    return <>{legacy}</>;
  }

  return (
    <ErrorBoundary identifier="delivery-settings-mf">
      <LazyComponent>
        <DeliverySettings backUrl={backUrl} />
      </LazyComponent>
    </ErrorBoundary>
  );
};

export default DeliverySettingsPage;
